import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  AppVersionContext,
  appVersionIsLoading,
  frontendVersionFromPackages,
  packageVersionsChanged,
} from './AppVersionContext';

const APP_VERSION_TIMEOUT_MS = 3000;

function AppVersionLayout() {
  const location = useLocation();
  const [packages, setPackages] = useState(null);
  const [checkedPath, setCheckedPath] = useState(null);
  const previousPackages = useRef(null);
  const loading = appVersionIsLoading(checkedPath, location.pathname);

  const fetchAppVersion = useCallback(async signal => {
    const appVersionUrl = `${process.env.PUBLIC_URL || ''}/versions.json`;
    try {
      const response = await fetch(`${appVersionUrl}?_ts=${Date.now()}`, {
        cache: 'no-store',
        signal,
      });
      if (response?.ok) {
        const body = await response.json();
        return body?.packages ?? null;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      APP_VERSION_TIMEOUT_MS,
    );
    fetchAppVersion(controller.signal).then(nextPackages => {
      clearTimeout(timeout);
      if (cancelled) return;
      if (nextPackages !== null) {
        if (packageVersionsChanged(previousPackages.current, nextPackages)) {
          setPackages(nextPackages);
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('_ts', String(Date.now()));
          window.location.href = currentUrl.toString();
          return;
        }
        previousPackages.current = nextPackages;
        setPackages(nextPackages);
      } else {
        setPackages(null);
      }
      setCheckedPath(location.pathname);
    });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [location.pathname, fetchAppVersion]);

  const value = useMemo(
    () => ({ appVersion: frontendVersionFromPackages(packages), loading }),
    [loading, packages],
  );

  return (
    <AppVersionContext.Provider value={value}>
      <Outlet />
    </AppVersionContext.Provider>
  );
}

export default AppVersionLayout;
